import { useMemo, useState } from 'react';
import { Card, Row, Col, Form, Button, Table, Badge, Nav, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faIndustry } from '@fortawesome/free-solid-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { useOpenLines } from '../../features/orders/useOrders';
import { http } from '../../features/jobs/api/http';
import { CABLE_NAMES_NO_ROPE, CABLE_COLORS } from '../../data/cableProducts.js';
import { formatProductLabel } from '../../helpers/productLabel';

// Phases that are decided at launch rather than held on the standard, so they
// have to be asked for rather than inferred.
const OPTIONAL_PHASES = ['unwinding', 'coiling', 'rewinding'];

const num = v => {
  const n = Number(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const m = v => `${Math.round(Number(v) || 0).toLocaleString()} m`;

const DESTINATION_TONE = { ORDER: 'primary', MIXED: 'warning', STOCK: 'secondary' };

/**
 * Who a drum is spoken for.
 *
 * `destination` is an object — kind, the stock/order split, the due date and the
 * order lines — not a label. Rendering it directly is what crashed this tab; the
 * useful part is the kind, and the date it is promised for.
 */
const destinationOf = token => {
  const d = token?.destination;
  if (!d) return <Badge bg="secondary" className="fs-10">stock</Badge>;
  if (typeof d === 'string') return <Badge bg="secondary" className="fs-10">{d}</Badge>;

  const kind = String(d.kind || 'STOCK').toUpperCase();
  return (
    <>
      <Badge bg={DESTINATION_TONE[kind] || 'secondary'} className="fs-10">
        {kind.toLowerCase()}
        {d.orderCount > 1 ? ` ×${d.orderCount}` : ''}
      </Badge>
      {d.dueDate ? <span className="ms-2 fs-10 text-body-tertiary">due {d.dueDate}</span> : null}
    </>
  );
};

/** Cables already part-made and sitting on a drum. */
function usePhysicalTokens() {
  return useQuery({
    queryKey: ['physical-tokens', 'planning'],
    queryFn: async () => {
      const { data } = await http.get('/api/redis/physical-tokens', {
        params: { includePlanned: true }
      });
      return data;
    }
  });
}

/**
 * Where the work to be planned comes from.
 *
 * Three sources, one list. Everything added here is a what-if: the plan is
 * recomputed from it, and nothing is written anywhere until somebody decides to
 * launch it for real.
 */
const PlanSources = ({ items, onAdd, onRemove, onClear, onChangeLength }) => {
  const [tab, setTab] = useState('orders');

  // ── ad-hoc product ──
  const [draft, setDraft] = useState({
    cableName: CABLE_NAMES_NO_ROPE[0] || 'CYYF',
    numberOfConductors: 3,
    section: 1.5,
    color: 'GREY',
    lengthM: '',
    extraPhases: []
  });

  const { data: linesData, isLoading: linesLoading } = useOpenLines({});
  const { data: tokensData, isLoading: tokensLoading } = usePhysicalTokens();

  const openLines = useMemo(() => {
    const raw = linesData?.data ?? linesData?.lines ?? linesData ?? [];
    return (Array.isArray(raw) ? raw : []).filter(l => num(l?.remainingToPlan) > 0);
  }, [linesData]);

  const tokens = useMemo(() => {
    const raw = tokensData?.tokens ?? tokensData?.data ?? [];
    return (Array.isArray(raw) ? raw : []).filter(t => num(t?.availableLength) > 0);
  }, [tokensData]);

  const addLine = line => {
    const p = line.product || {};
    onAdd({
      key: `${line.orderId}::${line.lineId}`,
      cableName: p.cableName,
      numberOfConductors: p.numberOfConductors,
      section: p.section,
      color: p.color,
      hasMixedConductorSections: p.hasMixedConductorSections,
      greenYellowSection: p.greenYellowSection,
      lengthM: num(line.remainingToPlan),
      dueDate: line.dueDate ?? null,
      label: formatProductLabel(p),
      origin: line.orderCode ? `order ${line.orderCode}` : 'order line'
    });
  };

  const addDraft = () => {
    if (!(num(draft.lengthM) > 0)) return;
    onAdd({
      key: `adhoc-${Date.now()}`,
      ...draft,
      section: num(draft.section),
      numberOfConductors: num(draft.numberOfConductors),
      lengthM: num(draft.lengthM),
      label: formatProductLabel(draft),
      origin: 'added by hand'
    });
    setDraft(d => ({ ...d, lengthM: '' }));
  };

  const toggleExtra = phase =>
    setDraft(d => ({
      ...d,
      extraPhases: d.extraPhases.includes(phase)
        ? d.extraPhases.filter(p => p !== phase)
        : [...d.extraPhases, phase]
    }));

  const totalM = items.reduce((a, i) => a + num(i.lengthM), 0);

  return (
    <Card className="mb-4">
      <Card.Header className="pb-0">
        <Nav variant="underline" activeKey={tab} onSelect={k => setTab(k || 'orders')}>
          <Nav.Item>
            <Nav.Link eventKey="orders">
              Order lines{' '}
              <Badge bg="secondary" className="fs-10">
                {openLines.length}
              </Badge>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="product">Add a product</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="tokens">
              In production{' '}
              <Badge bg="secondary" className="fs-10">
                {tokens.length}
              </Badge>
            </Nav.Link>
          </Nav.Item>
        </Nav>
      </Card.Header>

      <Card.Body>
        {tab === 'orders' && (
          <>
            <p className="fs-10 text-body-tertiary mb-2">
              Lines with length still to plan. Adding one asks the planner what it
              would take to make the rest.
            </p>
            {linesLoading ? (
              <Spinner size="sm" />
            ) : !openLines.length ? (
              <p className="text-body-tertiary fs-9 mb-0">Nothing left unplanned.</p>
            ) : (
              <div className="table-responsive" style={{ maxHeight: 260 }}>
                <Table size="sm" className="fs-9 mb-0">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Product</th>
                      <th className="text-end">To plan</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {openLines.map(line => (
                      <tr key={`${line.orderId}::${line.lineId}`}>
                        <td className="text-body-tertiary">{line.orderCode ?? '—'}</td>
                        <td>
                          {formatProductLabel(line.product || {})}{' '}
                          <span className="text-body-tertiary">{line.product?.color}</span>
                        </td>
                        <td className="text-end">{m(line.remainingToPlan)}</td>
                        <td className="text-end">
                          <Button size="sm" variant="phoenix-primary" onClick={() => addLine(line)}>
                            <FontAwesomeIcon icon={faPlus} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}

        {tab === 'product' && (
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Label className="fs-10 mb-1">Cable</Form.Label>
              <Form.Select
                size="sm"
                value={draft.cableName}
                onChange={e => setDraft({ ...draft, cableName: e.target.value })}
              >
                {CABLE_NAMES_NO_ROPE.map(n => (
                  <option key={n}>{n}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="fs-10 mb-1">Conductors</Form.Label>
              <Form.Control
                size="sm"
                type="number"
                min={1}
                value={draft.numberOfConductors}
                onChange={e => setDraft({ ...draft, numberOfConductors: e.target.value })}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="fs-10 mb-1">Section mm²</Form.Label>
              <Form.Control
                size="sm"
                value={draft.section}
                onChange={e => setDraft({ ...draft, section: e.target.value })}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="fs-10 mb-1">Colour</Form.Label>
              <Form.Select
                size="sm"
                value={draft.color}
                onChange={e => setDraft({ ...draft, color: e.target.value })}
              >
                {CABLE_COLORS.map(c => (
                  <option key={c}>{c}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="fs-10 mb-1">Length m</Form.Label>
              <Form.Control
                size="sm"
                value={draft.lengthM}
                onChange={e => setDraft({ ...draft, lengthM: e.target.value })}
                placeholder="5000"
              />
            </Col>
            <Col md={1}>
              <Button size="sm" variant="primary" className="w-100" onClick={addDraft}>
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </Col>
            <Col xs={12}>
              <div className="d-flex gap-3 align-items-center mt-1">
                <span className="fs-10 text-body-tertiary">Also plan:</span>
                {OPTIONAL_PHASES.map(p => (
                  <Form.Check
                    key={p}
                    inline
                    type="checkbox"
                    id={`extra-${p}`}
                    label={p}
                    className="fs-9"
                    checked={draft.extraPhases.includes(p)}
                    onChange={() => toggleExtra(p)}
                  />
                ))}
                <span className="fs-10 text-body-tertiary">
                  — chosen at launch, so the standard does not carry them
                </span>
              </div>
            </Col>
          </Row>
        )}

        {tab === 'tokens' && (
          <>
            <p className="fs-10 text-body-tertiary mb-2">
              <FontAwesomeIcon icon={faIndustry} className="me-1" />
              Cable already part-made and sitting on a drum. Shown so a plan can be
              read against what physically exists — this is the only source here
              describing reality rather than intention.
            </p>
            {tokensLoading ? (
              <Spinner size="sm" />
            ) : !tokens.length ? (
              <p className="text-body-tertiary fs-9 mb-0">No drums with length available.</p>
            ) : (
              <div className="table-responsive" style={{ maxHeight: 260 }}>
                <Table size="sm" className="fs-9 mb-0">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Phase</th>
                      <th>Colour</th>
                      <th className="text-end">Available</th>
                      <th>Promised to</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tokens.map((t, i) => (
                      <tr key={t.tokenId ?? i}>
                        <td>{t.productLabel ?? '—'}</td>
                        <td className="text-body-tertiary">
                          {String(t.phase || '').replaceAll('_', ' ')}
                        </td>
                        <td className="text-body-tertiary">{t.color ?? '—'}</td>
                        <td className="text-end">{m(t.availableLength)}</td>
                        <td>{destinationOf(t)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </Card.Body>

      {!!items.length && (
        <Card.Footer className="pt-2">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">
              To plan{' '}
              <span className="fw-normal text-body-tertiary fs-9">
                {items.length} · {m(totalM)}
              </span>
            </h6>
            <Button size="sm" variant="phoenix-secondary" onClick={onClear}>
              Clear
            </Button>
          </div>
          <Table size="sm" className="fs-9 mb-0">
            <tbody>
              {items.map(item => (
                <tr key={item.key}>
                  <td className="fw-semibold">{item.label}</td>
                  <td className="text-body-tertiary">{item.color}</td>
                  <td className="text-body-tertiary fs-10">{item.origin}</td>
                  <td style={{ width: 130 }}>
                    <Form.Control
                      size="sm"
                      value={item.lengthM}
                      onChange={e => onChangeLength(item.key, e.target.value)}
                    />
                  </td>
                  <td className="text-end" style={{ width: 50 }}>
                    <Button size="sm" variant="link" className="text-danger p-0" onClick={() => onRemove(item.key)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Footer>
      )}
    </Card>
  );
};

export default PlanSources;
